/**
 * Single source of truth for store product IDs and tier mapping, for BOTH stores.
 * Used by the client (StoreKit / Google Play registration) and the server (purchase verification).
 *
 * Google Play was added 2026-08-25, after enrolling in Play's billing choice programme. Android
 * previously sent every member straight to Stripe with no Play Billing offered at all, which is the
 * one arrangement Play's payments policy does not allow: outside the EEA you must offer Google's
 * billing alongside your own and let the member choose. See the memory note
 * `google-play-billing-choice-uk`.
 */

export type IapBillingInterval = "MONTHLY" | "YEARLY";
export type IapMembershipTier = "PREMIUM" | "ELITE";

export type IapProductDefinition = {
  planId: string;
  appleProductId: string;
  /**
   * Google Play subscription ID. Play requires lowercase letters, numbers, underscores and full
   * stops, so these deliberately do not mirror Apple's reverse-domain style. They must be created
   * in Play Console exactly as written here before an Android build can offer them, in the same way
   * the Apple IDs had to be created in App Store Connect.
   */
  googleProductId: string;
  tier: IapMembershipTier;
  interval: IapBillingInterval;
};

export const IAP_PRODUCT_DEFINITIONS: readonly IapProductDefinition[] = [
  {
    planId: "premium-monthly",
    appleProductId: "com.growthsync.app.premium.monthly.v2",
    googleProductId: "premium_monthly",
    tier: "PREMIUM",
    interval: "MONTHLY",
  },
  {
    planId: "premium-yearly",
    appleProductId: "com.growthsync.app.premium.yearly.v2",
    googleProductId: "premium_yearly",
    tier: "PREMIUM",
    interval: "YEARLY",
  },
  {
    planId: "elite-monthly",
    appleProductId: "com.growthsync.app.elite.monthly.v2",
    googleProductId: "elite_monthly",
    tier: "ELITE",
    interval: "MONTHLY",
  },
  {
    planId: "elite-yearly",
    appleProductId: "com.growthsync.app.elite.yearly.v2",
    googleProductId: "elite_yearly",
    tier: "ELITE",
    interval: "YEARLY",
  },
] as const;

/** Plan ID (e.g. premium-monthly) → App Store product ID — used by the iOS client. */
export const APPLE_PRODUCT_IDS: Record<string, string> = Object.fromEntries(
  IAP_PRODUCT_DEFINITIONS.map((d) => [d.planId, d.appleProductId]),
);

/** App Store product ID → tier + billing interval — used by /api/iap/verify-receipt. */
export const IAP_PRODUCT_TIER_MAP: Record<string, { tier: string; interval: string }> =
  Object.fromEntries(
    IAP_PRODUCT_DEFINITIONS.map((d) => [
      d.appleProductId,
      { tier: d.tier, interval: d.interval },
    ]),
  );

/** Maps a GrowthSync plan ID to its Apple StoreKit product ID; null if none. */
export function getAppleProductId(planId: string): string | null {
  return APPLE_PRODUCT_IDS[planId] ?? null;
}

/** Plan ID (e.g. premium-monthly) → Google Play subscription ID — used by the Android client. */
export const GOOGLE_PRODUCT_IDS: Record<string, string> = Object.fromEntries(
  IAP_PRODUCT_DEFINITIONS.map((d) => [d.planId, d.googleProductId]),
);

/** Maps a GrowthSync plan ID to its Google Play subscription ID; null if none. */
export function getGoogleProductId(planId: string): string | null {
  return GOOGLE_PRODUCT_IDS[planId] ?? null;
}

/**
 * Store product ID → tier + interval, covering BOTH stores.
 *
 * Deliberately one lookup rather than one per store: verification code should not have to know
 * which shop a purchase came from to work out what it entitles somebody to, and a second map would
 * be a second place to forget to add a plan.
 */
export const STORE_PRODUCT_TIER_MAP: Record<string, { tier: string; interval: string }> =
  Object.fromEntries(
    IAP_PRODUCT_DEFINITIONS.flatMap((d) => [
      [d.appleProductId, { tier: d.tier, interval: d.interval }],
      [d.googleProductId, { tier: d.tier, interval: d.interval }],
    ]),
  );
